
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface UserLevel {
  user_id: string;
  current_level: 'Inactive' | 'Saver' | 'Organizer' | 'Discoverer' | 'Curator';
  level_number: number;
  saves_7d: number;
  organizes_7d: number;
  discovers_7d: number;
}

export const useUserLevel = () => {
  const { user } = useAuth();
  const [userLevel, setUserLevel] = useState<UserLevel | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserLevel = async () => {
    if (!user) {
      setUserLevel(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_user_current_level', {
        user_id_param: user.id
      });

      if (error) {
        console.error('Error fetching user level:', error);
        setUserLevel(null);
      } else if (data && data.length > 0) {
        const levelData = data[0];
        // Type assertion to ensure the current_level matches our union type
        const typedLevel: UserLevel = {
          user_id: levelData.user_id,
          current_level: levelData.current_level as UserLevel['current_level'],
          level_number: levelData.level_number,
          saves_7d: levelData.saves_7d,
          organizes_7d: levelData.organizes_7d,
          discovers_7d: levelData.discovers_7d
        };
        setUserLevel(typedLevel);
      } else {
        // Default to Inactive if no data
        setUserLevel({
          user_id: user.id,
          current_level: 'Inactive',
          level_number: 0,
          saves_7d: 0,
          organizes_7d: 0,
          discovers_7d: 0
        });
      }
    } catch (error) {
      console.error('Error in fetchUserLevel:', error);
      setUserLevel(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserLevel();
  }, [user]);

  // Set up real-time subscription to update level when new events are tracked
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('user-level-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_activity_events',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Refresh user level when new activity is tracked
          fetchUserLevel();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    userLevel,
    isLoading,
    refreshLevel: fetchUserLevel
  };
};
